import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type Layout = 'tabs' | 'split'
type ActiveTab = 'map' | 'graph'
type SidebarTab = 'entities' | 'geofences' | 'insights' | 'investigations'
type GraphLayout =
  | 'custom'
  | 'fcose'
  | 'clusters'
  | 'cola'
  | 'grid'
  | 'circle'
  | 'breadthfirst'
  | 'concentric'

interface PathHighlight {
  nodeIds: string[]
  edgeIds: string[]
}

interface MapTarget {
  lat: number
  lon: number
  zoom: number
}

interface CockpitState {
  // Layout
  layout: Layout
  setLayout: (layout: Layout) => void
  activeTab: ActiveTab
  setActiveTab: (tab: ActiveTab) => void

  // Timeline drawer
  timelineOpen: boolean
  toggleTimeline: () => void
  setTimelineOpen: (open: boolean) => void

  // Sidebar
  sidebarOpen: boolean
  setSidebarOpen: (open: boolean) => void
  sidebarTab: SidebarTab
  setSidebarTab: (tab: SidebarTab) => void

  // Cross-view filtering (applies to BOTH map and graph)
  focusedEntityIds: Set<string>
  setFocusedEntities: (ids: string[]) => void
  clearFocus: () => void

  // Selection sync between views
  selectedNodeId: string | null
  setSelectedNodeId: (id: string | null) => void

  // Graph layout
  graphLayout: GraphLayout
  setGraphLayout: (layout: GraphLayout) => void

  // Path highlighting (from agent tools)
  pathHighlight: PathHighlight | null
  setPathHighlight: (nodeIds: string[], edgeIds: string[]) => void
  clearPathHighlight: () => void

  // Map target (for agent-driven flyTo)
  mapTarget: MapTarget | null
  setMapTarget: (target: MapTarget | null) => void
}

export const useCockpitStore = create<CockpitState>()(
  persist(
    set => ({
      // Layout defaults
      layout: 'tabs',
      setLayout: layout => set({ layout }),
      activeTab: 'map',
      setActiveTab: tab => set({ activeTab: tab }),

      // Timeline drawer
      timelineOpen: false,
      toggleTimeline: () => set(state => ({ timelineOpen: !state.timelineOpen })),
      setTimelineOpen: open => set({ timelineOpen: open }),

      // Sidebar
      sidebarOpen: true,
      setSidebarOpen: open => set({ sidebarOpen: open }),
      sidebarTab: 'entities',
      setSidebarTab: tab => set({ sidebarTab: tab }),

      // Cross-view filtering
      focusedEntityIds: new Set<string>(),
      setFocusedEntities: ids => set({ focusedEntityIds: new Set(ids) }),
      clearFocus: () => set({ focusedEntityIds: new Set<string>() }),

      // Selection sync
      selectedNodeId: null,
      setSelectedNodeId: id => set({ selectedNodeId: id }),

      // Graph layout
      graphLayout: 'fcose',
      setGraphLayout: layout => set({ graphLayout: layout }),

      // Path highlighting
      pathHighlight: null,
      setPathHighlight: (nodeIds, edgeIds) => set({ pathHighlight: { nodeIds, edgeIds } }),
      clearPathHighlight: () => set({ pathHighlight: null }),

      // Map target
      mapTarget: null,
      setMapTarget: target => set({ mapTarget: target }),
    }),
    {
      name: 'cockpit-layout',
      partialize: state => ({
        layout: state.layout,
        sidebarOpen: state.sidebarOpen,
        sidebarTab: state.sidebarTab,
        graphLayout: state.graphLayout,
      }),
    }
  )
)

// Graph layout options for UI
export const GRAPH_LAYOUT_OPTIONS = [
  { id: 'fcose', name: 'Force-directed', description: 'High-quality force layout' },
  { id: 'clusters', name: 'Clusters', description: 'Group by community' },
  { id: 'cola', name: 'Cola', description: 'Edge-weighted layout' },
  { id: 'grid', name: 'Grid', description: 'Organized grid' },
  { id: 'circle', name: 'Circle', description: 'Circular arrangement' },
  { id: 'breadthfirst', name: 'Hierarchical', description: 'Tree-like hierarchy' },
  { id: 'concentric', name: 'Concentric', description: 'Concentric circles' },
] as const
